import { PauseIcon, PlayIcon } from '@heroicons/react/24/solid';
import MuxAudio from '@mux/mux-audio-react';
import { clsx } from 'clsx';
import {
  MediaControlBar,
  MediaController,
  MediaPlayButton,
} from 'media-chrome/react';
import Image from 'next/image';
import {
  CSSProperties,
  ReactEventHandler,
  RefObject,
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

import { Button, HeroIcon, Typography } from '@/components/atoms';
import {
  PlayerAddToButton,
  PlayerExpandPlayerButton,
  PlayerMuteButton,
  PlayerNextButton,
  PlayerPreviousButton,
  PlayerScrubber,
  PlayerSettingsMenuButtonAudio,
  PlayerVolumeRange,
} from '@/components/atoms/PlayerControl';
import { defaultButtonStyling } from '@/components/atoms/PlayerControl/util';
import { LikeButton } from '@/components/molecules';
import { useAnalytics } from '@/lib/contexts/analytics';
import {
  AssetNormalized,
  CompilationNormalized,
  CompilationType,
  TrackNormalized,
} from '@/lib/contexts/media-controller/util';
import { useMediaPauseRequest } from '@/lib/hooks/media-controller/useMediaPauseRequest';
import { getPackageInfo } from '@/lib/utils/getPackageInfo';
import { type Duration, secondsToDuration } from '@/lib/utils/time-formatting';

import type { TrackFileType } from '@/lib/api';
import type { MediaPlayButton as MediaPlayButtonElement } from 'media-chrome';
import type {
  ContentParentType,
  SendStreamMarkerType,
} from '@/lib/contexts/analytics';

import type { UUID } from '@/types';

type PlayerState =
  | 'idle'
  | 'loading'
  | 'playing'
  | 'paused'
  | 'seeking'
  | 'error';

type Metadata = {
  compilation: CompilationNormalized;
  track: TrackNormalized;
};

export interface MiniPlayerHandles {
  load: (
    metadata: Metadata | undefined,
    format: TrackFileType | undefined,
  ) => void;
  play: () => void;
  pause: () => void;
  stop: () => void;
}

export interface AudioRenditions {
  type: TrackFileType;
}

interface PositionMarkers {
  start: number;
  end: number;
}

interface Props {
  ref?: RefObject<MiniPlayerHandles | null>;
  tenantId?: string;
  muxDataId?: string;
  disablePrevious?: boolean;
  disableNext?: boolean;
  availableFormats: TrackFileType[];
  preferredFormat?: TrackFileType;
  getTrackMetadata?: (
    compilation: CompilationNormalized,
    track: TrackNormalized,
    format: TrackFileType,
  ) => Promise<void | AssetNormalized>;
  onInfoClick?: () => void;
  onPlaying?: () => void;
  onPause?: () => void;
  onPauseExplicit?: () => void;
  onEnded?: () => void;
  onPrevious?: () => void;
  onNext?: () => void;
  onDurationChange?: (duration: Duration) => void;
  onFormatSelect?: (format: TrackFileType) => void;
  onError?: (error: Error) => void;
}

export default function MiniPlayer({
  ref,
  tenantId,
  muxDataId,
  disablePrevious = false,
  disableNext = false,
  availableFormats,
  preferredFormat,
  getTrackMetadata = () => Promise.resolve(),
  onInfoClick,
  onPlaying = () => {},
  onPause = () => {},
  onPauseExplicit,
  onEnded = () => {},
  onPrevious,
  onNext,
  onDurationChange = () => {},
  onFormatSelect = () => {},
  onError = () => {},
}: Props) {
  const [metadata, setMetadata] = useState<Metadata>();
  const [assetUrl, setAssetUrl] = useState<string>();
  const [duration, setDuration] = useState<Duration>({
    seconds: 0,
    minutes: 0,
  });
  const [playerState, setPlayerState] = useState<PlayerState>('idle');
  const [streamId, setStreamId] = useState<UUID>();
  const [positionMarkers, setPositionMarkers] = useState<PositionMarkers>();

  const muxAudioRef = useRef<HTMLAudioElement>(null);
  const cumulativeTimeRef = useRef<number>(0);
  const lastPlayMarkerRef = useRef<number>(null);
  const intervalSentRef = useRef<boolean>(false);
  const seekDebouncerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isScrubbingRef = useRef<boolean>(false);
  const ignoreTeardownEventsRef = useRef<boolean>(false);
  const terminalMarkerSentRef = useRef<boolean>(false);
  const pauseRequestRef =
    useMediaPauseRequest<MediaPlayButtonElement>(onPauseExplicit);

  const { getSessionId, generateStreamId, track } = useAnalytics();

  useImperativeHandle(ref, () => ({
    load: (metadata, format) => stopAndLoadAudio(metadata, format),
    play: () => muxAudioRef.current?.play(),
    pause: () => muxAudioRef.current?.pause(),
    stop,
  }));

  // TODO: This should be revamped so that the normalizer does this on the onset
  // of queueing up the compilation
  const resolveEventType = (type: CompilationType): ContentParentType => {
    switch (type) {
      case 'personal_favourites': {
        return 'favorite-tracks';
      }
      case 'personal_recently_viewed': {
        return 'recent-tracks';
      }
      // This is resolved as a playlist as the analytics EP doesn't
      // know what a personal playlist is
      case 'personal_playlist': {
        return 'playlist';
      }
      default: {
        return type;
      }
    }
  };

  const sendInitBeacon = useCallback(
    (
      compilation: CompilationNormalized,
      trackItem: TrackNormalized,
      format: TrackFileType,
    ) => {
      const { id: compilationId, type } = compilation;
      const { trackId } = trackItem;
      const nextStreamId = generateStreamId();

      setStreamId(nextStreamId);
      setPositionMarkers({ start: 0, end: 0 });
      terminalMarkerSentRef.current = false;

      track({
        type: 'stream_init',
        streamId: nextStreamId,
        contentPlayed: {
          id: trackId,
          type: type === 'podcast' ? 'chapter' : 'track',
          rendition: format,
        },
        contentParent: {
          id: compilationId,
          type: resolveEventType(type),
        },
      });
    },
    [generateStreamId, track],
  );

  const sendStreamBeacon = useCallback(
    (event: SendStreamMarkerType) => {
      if (!streamId || !preferredFormat || !positionMarkers) return;

      const time = lastPlayMarkerRef.current
        ? Date.now() - lastPlayMarkerRef.current
        : 0;

      cumulativeTimeRef.current += time;
      lastPlayMarkerRef.current = event === 'interval' ? Date.now() : null;

      const { end } = positionMarkers;
      const timer = {
        start: end,
        end: muxAudioRef.current?.currentTime ?? -1,
      };

      if (event === 'seeked' && timer.start === timer.end) return;

      track({
        type: 'stream_marker',
        streamId,
        markerType: event,
        timer,
        cumulativeTime: Math.round(cumulativeTimeRef.current / 1000),
        rendition: preferredFormat,
      });

      setPositionMarkers(timer);
    },
    [positionMarkers, preferredFormat, streamId, track],
  );

  const sendTerminalStreamBeacon = useCallback(
    (event: Extract<SendStreamMarkerType, 'ended' | 'error'>) => {
      if (terminalMarkerSentRef.current) return;

      terminalMarkerSentRef.current = true;
      sendStreamBeacon(event);
    },
    [sendStreamBeacon],
  );

  const stop = useCallback(() => {
    if (!muxAudioRef.current) return;

    ignoreTeardownEventsRef.current = Boolean(
      streamId || assetUrl || muxAudioRef.current.currentSrc,
    );
    muxAudioRef.current.src = '';
    setAssetUrl(undefined);
    setPlayerState('idle');
    sendTerminalStreamBeacon('ended');
    cumulativeTimeRef.current = 0;
    intervalSentRef.current = false;
  }, [assetUrl, sendTerminalStreamBeacon, streamId]);

  const stopAndLoadAudio = async (
    metadata?: Metadata,
    format?: TrackFileType,
  ) => {
    if (!muxAudioRef.current || !metadata || !format) return;

    const { compilation, track } = metadata;
    setMetadata(metadata);
    setPlayerState('loading');

    const asset = await getTrackMetadata(compilation, track, format);

    if (!asset) {
      setPlayerState('idle');
      return;
    }

    const shouldStopExistingAudio = Boolean(
      streamId || assetUrl || muxAudioRef.current.currentSrc,
    );

    if (shouldStopExistingAudio) {
      stop();
    } else {
      cumulativeTimeRef.current = 0;
      intervalSentRef.current = false;
      terminalMarkerSentRef.current = false;
    }

    setAssetUrl(asset.url);

    sendInitBeacon(compilation, track, format);
  };

  const handleOnLoadedMetadata: ReactEventHandler<HTMLAudioElement> =
    useCallback(
      event => {
        ignoreTeardownEventsRef.current = false;
        const duration = secondsToDuration(event.currentTarget.duration);
        setDuration(duration);
        onDurationChange(duration);
      },
      [onDurationChange],
    );

  const handleOnPlaying = useCallback(() => {
    ignoreTeardownEventsRef.current = false;
    setPlayerState('playing');
    onPlaying();

    lastPlayMarkerRef.current = Date.now();
  }, [onPlaying]);

  const handleOnPause = useCallback(() => {
    if (ignoreTeardownEventsRef.current) return;

    setPlayerState('paused');
    onPause();

    sendStreamBeacon('pause');
  }, [sendStreamBeacon, onPause]);

  const handleOnEnded = useCallback(() => {
    if (ignoreTeardownEventsRef.current) return;
    if (terminalMarkerSentRef.current) return;

    setPlayerState('idle');
    onEnded();

    sendTerminalStreamBeacon('ended');
  }, [sendTerminalStreamBeacon, onEnded]);

  const handleOnSeeked = useCallback(() => {
    if (ignoreTeardownEventsRef.current) return;
    if (isScrubbingRef.current) return;

    if (seekDebouncerRef.current) {
      clearTimeout(seekDebouncerRef.current);
    }

    seekDebouncerRef.current = setTimeout(() => {
      sendStreamBeacon('seeked');
    }, 100);
  }, [sendStreamBeacon]);

  const handleOnError: ReactEventHandler<HTMLAudioElement> = useCallback(
    event => {
      if (ignoreTeardownEventsRef.current) return;
      if (terminalMarkerSentRef.current) return;
      // Browsers can dispatch an error for the empty source while React swaps
      // tracks. It is a teardown/loading artefact, not a playback failure.
      if (!event.currentTarget.currentSrc) return;

      // MEDIA_ERR_ABORTED is expected when previous/next or another play CTA
      // replaces a request that is still loading.
      if (event.currentTarget.error?.code === 1) {
        return;
      }

      setPlayerState('error');
      onError(new Error('Audio playback failed'));
      sendTerminalStreamBeacon('error');
    },
    [onError, sendTerminalStreamBeacon],
  );

  const handleOnTimeUpdate = useCallback(() => {
    if (ignoreTeardownEventsRef.current) return;
    if (intervalSentRef.current) return;
    if (!lastPlayMarkerRef.current) return;
    const virtualCumulativeTime =
      cumulativeTimeRef.current + (Date.now() - lastPlayMarkerRef.current);

    if (virtualCumulativeTime > 30000) {
      intervalSentRef.current = true;
      sendStreamBeacon('interval');
    }
  }, [sendStreamBeacon]);

  const renderMuxAudio = useCallback(() => {
    return (
      <MuxAudio
        ref={muxAudioRef}
        envKey={muxDataId}
        slot="media"
        src={assetUrl}
        type="video/mp4"
        streamType="on-demand"
        metadata={{
          video_id: metadata?.track.trackId,
          video_title: metadata?.track.trackTitle,
          video_brand: tenantId,
          video_content_type: metadata
            ? resolveEventType(metadata.compilation.type)
            : null,
          // We override the Mux player instance id with the stream id from Vialma
          player_instance_id: streamId,
          video_client_application_name: 'web-app',
          video_client_application_version: getPackageInfo().version,
          view_session_id: getSessionId(),
        }}
        disableTracking={!assetUrl}
        onLoadedMetadata={handleOnLoadedMetadata}
        onPlaying={handleOnPlaying}
        onPause={handleOnPause}
        onEnded={handleOnEnded}
        onSeeking={handleOnSeeked}
        onTimeUpdate={handleOnTimeUpdate}
        onError={handleOnError}
        controls
        autoPlay
      />
    );
  }, [
    assetUrl,
    getSessionId,
    handleOnEnded,
    handleOnError,
    handleOnLoadedMetadata,
    handleOnPause,
    handleOnPlaying,
    handleOnSeeked,
    handleOnTimeUpdate,
    metadata,
    tenantId,
    streamId,
    muxDataId,
  ]);

  const isInteractable = playerState !== 'idle' && playerState !== 'loading';
  const imageUrl = metadata?.track.image ?? metadata?.compilation.images.small;

  return (
    <div className="bg-(--player-background-color-default) py-3 shadow-[0px_-4px_20px_0px_rgba(0,_0,_0,_0.3)] rounded-t-lg">
      <div className="mx-auto px-5">
        <MediaController
          id="mini_player"
          className="block w-full leading-normal"
          style={
            {
              '--media-background-color':
                'var(--player-background-color-default)',
            } as CSSProperties
          }
          audio
        >
          {renderMuxAudio()}
          <MediaControlBar
            className="block"
            style={
              {
                '--media-text-color': 'var(--player-text-color-primary)',
              } as CSSProperties
            }
          >
            <div className="flex flex-col gap-2">
              <div className="hidden sm:flex">
                <PlayerScrubber
                  duration={duration}
                  onMouseDown={() => (isScrubbingRef.current = true)}
                  onMouseUp={() => {
                    isScrubbingRef.current = false;
                    handleOnSeeked();
                  }}
                />
              </div>
              <div className="flex flex-row-reverse sm:flex-row sm:justify-between gap-4">
                <div className="flex shrink-0">
                  <div className="flex gap-1">
                    <div
                      className={clsx({
                        'sm:flex items-center': onPrevious !== undefined,
                        'hidden select-none': true,
                      })}
                    >
                      <PlayerPreviousButton
                        size="md"
                        disabled={!isInteractable || disablePrevious}
                        onClick={onPrevious}
                      />
                    </div>
                    <MediaPlayButton
                      ref={pauseRequestRef}
                      style={defaultButtonStyling}
                    >
                      <span slot="play">
                        <Button aria-hidden="true" tabIndex={-1} size="md">
                          <HeroIcon icon={PlayIcon} />
                        </Button>
                      </span>
                      <span slot="pause">
                        <Button aria-hidden="true" tabIndex={-1} size="md">
                          <HeroIcon icon={PauseIcon} />
                        </Button>
                      </span>
                    </MediaPlayButton>
                    <div
                      className={clsx({
                        'sm:flex items-center': onNext !== undefined,
                        'hidden select-none': true,
                      })}
                    >
                      <PlayerNextButton
                        disabled={!isInteractable || disableNext}
                        onClick={onNext}
                      />
                    </div>
                  </div>
                </div>
                <div className="flex gap-4 grow sm:grow-0 sm:w-[64%]">
                  <div className="hidden sm:flex sm:items-center">
                    {metadata && imageUrl && imageUrl !== '' && (
                      <div className="relative size-[60px]">
                        <Image
                          className="rounded-sm size-full cover object-cover"
                          src={imageUrl}
                          alt={metadata.track.trackTitle || ''}
                          sizes="60px"
                          fill
                          unoptimized
                        />
                      </div>
                    )}
                  </div>
                  <button
                    className="grow text-left sm:pointer-events-none cursor-pointer sm:cursor-auto"
                    onClick={onInfoClick}
                  >
                    <div className="grid grid-cols-1 flex-1 min-w-auto">
                      <Typography.Text
                        className="whitespace-nowrap truncate text-(--player-text-color-secondary)"
                        size="sm"
                      >
                        {metadata?.track.trackComposer}
                      </Typography.Text>
                      <Typography.Text
                        className="whitespace-nowrap truncate text-(--player-text-color-primary)"
                        size="md"
                        weight="semibold"
                      >
                        {metadata?.track.trackTitle}
                      </Typography.Text>
                      <Typography.Text
                        className="whitespace-nowrap truncate text-(--player-text-color-secondary)"
                        size="sm"
                      >
                        {metadata?.track.trackPerformers}
                      </Typography.Text>
                    </div>
                  </button>
                  <div className="flex items-center">
                    {metadata && (
                      <>
                        <LikeButton
                          contentId={Number(metadata.track.trackId)}
                          disabled={metadata.compilation.type === 'podcast'}
                          contentType="Track"
                          iconOnly
                        />
                        <PlayerAddToButton
                          contentId={Number(metadata.track.trackId)}
                        />
                      </>
                    )}
                    {onInfoClick && (
                      <div className="hidden sm:flex">
                        <PlayerExpandPlayerButton onClick={onInfoClick} />
                      </div>
                    )}
                  </div>
                </div>
                <div className="hidden sm:flex justify-end items-center gap-4">
                  <div className="flex">
                    <PlayerMuteButton />
                    <PlayerVolumeRange />
                  </div>
                  {availableFormats.length > 1 && (
                    <div className="relative">
                      <PlayerSettingsMenuButtonAudio
                        availableFormats={availableFormats}
                        preferredRendition={preferredFormat}
                        handleOnRenditionSelect={onFormatSelect}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </MediaControlBar>
        </MediaController>
      </div>
    </div>
  );
}
